import * as React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Field,
  Switch,
  DialogTrigger,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { Incident } from '#root/queries/incidents';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { formatToLocalTime, createLocalDate } from '#root/services/utils';

interface IncidentDialogProps {
  open: boolean;
  onOpenChange: (event: any, data: { open: boolean }) => void;
  onSave: (incident: Partial<Incident>) => void;
  incident?: Incident;
  title: string;
}

export const IncidentDialog: React.FC<IncidentDialogProps> = ({ open, onOpenChange, onSave, incident, title }) => {
  const [name, setName] = React.useState(incident?.name || '');
  const [address, setAddress] = React.useState(incident?.address || '');
  const [startDate, setStartDate] = React.useState<Date>(createLocalDate(incident?.startDate) || new Date());
  const [endDate, setEndDate] = React.useState<Date>(createLocalDate(incident?.endDate) || new Date());
  const [isActive, setIsActive] = React.useState(incident?.isActive ?? true);

  const resetForm = React.useCallback(() => {
    if (incident) {
      setName(incident.name || '');
      setAddress(incident.address || '');
      setStartDate(createLocalDate(incident.startDate) || new Date());
      setEndDate(createLocalDate(incident.endDate) || new Date());
      setIsActive(incident.isActive ?? true);
    } else {
      setName('');
      setAddress('');
      setStartDate(new Date());
      setEndDate(new Date());
      setIsActive(true);
    }
  }, [incident]);

  React.useEffect(() => {
    resetForm();
  }, [resetForm]);

  React.useEffect(() => {
    if (endDate < startDate) {
      setEndDate(startDate);
    }
  }, [startDate, endDate]);

  const handleSave = () => {
    const adjustedStartDate = createLocalDate(startDate, false);
    const adjustedEndDate = createLocalDate(endDate, false);
    
    onSave({
      name,
      address,
      startDate: adjustedStartDate,
      endDate: adjustedEndDate,
      isActive,
    });
    
    // Close dialog and reset form
    onOpenChange?.(undefined, { open: false });
    setName('');
    setAddress('');
    setStartDate(new Date());
    setEndDate(new Date());
    setIsActive(true);
  };

  const handleCancel = (event: any) => {
    resetForm();
  };

  const formatDate = (date?: Date): string => {
    return date ? formatToLocalTime(date, 'date', false) : '';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogSurface className="incident-dialog">
        <DialogBody>
          <DialogTitle action={
            <DialogTrigger action="close">
              <Button appearance="subtle" icon={<Dismiss20Regular />} />
            </DialogTrigger>
          }>
            {title}
          </DialogTitle>
          <DialogContent>
            <form>
              <Field label="Incident Name" required>
                <Input
                  value={name}
                  placeholder="Please enter incident name"
                  onChange={(e, data) => setName(data.value)}
                />
              </Field>
              <Field label="Incident Address" required>
                <Input
                  value={address}
                  placeholder="Please enter incident address"
                  onChange={(e, data) => setAddress(data.value)}
                />
              </Field>
              <div className='field-two-columns'>
                <Field label="Start Date" required>
                  <DatePicker
                    name="startDate"
                    onSelectDate={(date: Date | null | undefined) => date && setStartDate(date)}
                    formatDate={formatDate}
                    value={startDate}
                    allowTextInput
                    placeholder="MM/DD/YYYY"
                    className="date-picker"
                  />
                </Field>
                <Field label="End Date" required>
                  <DatePicker
                    name="endDate"
                    onSelectDate={(date: Date | null | undefined) => date && setEndDate(date)}
                    formatDate={formatDate}
                    value={endDate}
                    allowTextInput
                    placeholder="MM/DD/YYYY"
                    minDate={startDate}
                    className="date-picker"
                  />
                </Field>  
              </div>
            </form>
          </DialogContent>
          <DialogActions className="dialog-actions-between">
            <Switch
              label="Is the incident active?"
              checked={isActive}
              onChange={(e, data) => setIsActive(data.checked)}
            />
            <div className="actions-right">
              <Button 
                appearance="primary" 
                onClick={handleSave}
                disabled={!name.trim() || !address.trim()}
              >
                Save
              </Button>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" onClick={handleCancel}>Cancel</Button>
              </DialogTrigger>
            </div>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
